/**
 * @fileoverview Página responsável por organizar tarefas e pendências internas.
 */

import React, { useState, useEffect } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import { Pencil, Trash2 as Trash } from "lucide-react";
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '1rem',
    backgroundColor: theme.mode === 'light' ? '#fafafa' : '#1A1A1A',
    minHeight: 'calc(100vh - 120px)',
    maxHeight: 'calc(100vh - 120px)',
    padding: '1rem',
    overflow: 'hidden',
    boxSizing: 'border-box'
  },
  inputContainer: {
    display: 'flex',
    width: '100%',
    marginBottom: '1rem',
    gap: '1rem',
    flexShrink: 0
  },
  input: {
    flexGrow: 1,
    '& .MuiInputLabel-root': {
      color: theme.mode === 'light' ? '#000' : '#eee',
    },
    '& .MuiOutlinedInput-root': {
      color: theme.mode === 'light' ? '#000' : '#eee',
      '& fieldset': {
        borderColor: theme.mode === 'light' ? '#000' : '#eee',
      },
      '&:hover fieldset': {
        borderColor: theme.mode === 'light' ? '#000' : '#eee',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.mode === 'light' ? '#000' : '#eee',
      },
    },
  },
  addButton: {
    backgroundColor: '#3D5CFD',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2A4BDB',
    },
  },
  listContainer: {
    width: '100%',
    maxHeight: '60vh',
    marginTop: '1rem',
    backgroundColor: theme.mode === 'light' ? '#f5f5f5' : '#2C2C2C',
    borderRadius: '8px',
    border: `1px solid ${theme.mode === 'light' ? '#e0e0e0' : '#404040'}`,
    overflowY: 'auto',
    padding: '8px',
  },
  listItem: {
    backgroundColor: theme.mode === 'light' ? '#fff' : '#3A3A3A',
    margin: '4px 0',
    borderRadius: '6px',
    border: `1px solid ${theme.mode === 'light' ? '#e0e0e0' : '#404040'}`,
    minHeight: '60px',
    '&:hover': {
      backgroundColor: theme.mode === 'light' ? '#f8f8f8' : '#404040',
    }
  },
  listItemText: {
    '& .MuiListItemText-primary': {
      color: theme.mode === 'light' ? '#000' : '#eee',
      fontSize: '16px',
      fontWeight: '500'
    },
    '& .MuiListItemText-secondary': {
      color: theme.mode === 'light' ? '#666' : '#88898D',
      fontSize: '14px'
    }
  },
  iconButton: {
    color: '#88898D',
    '&:hover': {
      backgroundColor: theme.mode === 'light' ? '#f0f0f0' : '#404040',
    }
  }
}));

const ToDoList = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [task, setTask] = useState('');
  const [tasks, setTasks] = useState([]);
  const [editIndex, setEditIndex] = useState(-1);

  useEffect(() => {
    const savedTasks = localStorage.getItem('tasks');
    if (savedTasks) {
      setTasks(JSON.parse(savedTasks));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('tasks', JSON.stringify(tasks));
  }, [tasks]);

  const handleTaskChange = (event) => {
    setTask(event.target.value);
  };

  const handleAddTask = () => {
    if (!task.trim()) {
      // Impede que o usuário crie uma tarefa sem texto
      return;
    }

    const now = new Date();
    if (editIndex >= 0) {
      // Editar tarefa existente
      const newTasks = [...tasks];
      newTasks[editIndex] = {text: task, updatedAt: now, createdAt: newTasks[editIndex].createdAt};
      setTasks(newTasks);
      setTask('');
      setEditIndex(-1);
    } else {
      // Adicionar nova tarefa
      setTasks([...tasks, {text: task, createdAt: now, updatedAt: now}]);
      setTask('');
    }
  };

  const handleEditTask = (index) => {
    setTask(tasks[index].text);
    setEditIndex(index);
  };

  const handleDeleteTask = (index) => {
    const newTasks = [...tasks];
    newTasks.splice(index, 1);
    setTasks(newTasks);
  };

  return (
    <div className={classes.root}>
      <div className={classes.inputContainer}>
        <TextField
          className={classes.input}
          label={i18n.t('todolist.input')}
          value={task}
          onChange={handleTaskChange}
          variant="outlined"
        />
        <Button 
          variant="contained" 
          className={classes.addButton}
          onClick={handleAddTask}
        >
          {editIndex >= 0 ? i18n.t('todolist.buttons.save') : i18n.t('todolist.buttons.add')}
        </Button>
      </div>
      <div className={classes.listContainer}>
        <List>
          {tasks.map((task, index) => (
            <ListItem key={index} className={classes.listItem}>
              <ListItemText 
                primary={task.text} 
                secondary={task.updatedAt.toLocaleString()} 
                className={classes.listItemText}
              />
              <ListItemSecondaryAction>
                <IconButton 
                  onClick={() => handleEditTask(index)}
                  className={classes.iconButton}
                >
                  <Pencil size={18} strokeWidth={1.5} color="#5490ff" />
                </IconButton>
                <IconButton 
                  onClick={() => handleDeleteTask(index)}
                  className={classes.iconButton}
                >
                  <Trash size={18} strokeWidth={1.5} color="#c72b40" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </div>
    </div>
  );
};


export default ToDoList;
